import { useState } from 'react';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { useMatchMedia } from 'src/hooks/useMatchMedia';

import { DashboardSectionWrapper } from '../DashboardSectionWrapper';
import { InsightsSection } from '../InsightsSection/InsightsSection';
import { ChartHeader, ChartHeaderInput } from './ChartHeader';
import { ChartType, ChartTypeSelector } from './ChartTypeSelector';
import { ChartTypeSelectorDropdown } from './ChartTypeSelectorDropdown';
import * as styles from './DataInsightsChart.css';
import { DataInsightsProvider, useDataInsights } from './DataInsightsContext';
import { MarketshareChart } from './MarketshareChart/MarketshareChart';
import { PnlChart } from './PnLChart/PnLChart';
import { RevenueChart } from './RevenueChart/RevenueChart';
import { SalesChart } from './SalesChart/SalesChart';

export const DataInsightsChartSection = () => {
  const [chartType, setChartType] = useState<ChartType>('revenue');

  return (
    <DataInsightsProvider>
      <DataInsightsChartContent
        chartType={chartType}
        setChartType={setChartType}
      />
    </DataInsightsProvider>
  );
};

const DataInsightsChartContent = ({
  chartType,
  setChartType,
}: {
  chartType: ChartType;
  setChartType: React.Dispatch<React.SetStateAction<ChartType>>;
}) => {
  const { chartData, marketplaceChartData } = useDataInsights();
  const isMobile = useMatchMedia('mobile');

  const renderHeader = (input: ChartHeaderInput) => {
    return (
      <DashboardSectionWrapper fixed>
        <Stack
          direction="row"
          justifyContent={input.value ? 'spaceBetween' : 'end'}
          alignItems="center"
          style={{
            width: '100%',
            marginBottom: vars.spacing['xl'],
            height: '52px',
          }}
        >
          <ChartHeader {...input} />
          <Stack>
            {isMobile ? (
              <ChartTypeSelectorDropdown
                chartType={chartType}
                setChartType={setChartType}
              />
            ) : (
              <ChartTypeSelector
                chartType={chartType}
                setChartType={setChartType}
              />
            )}
          </Stack>
        </Stack>
      </DashboardSectionWrapper>
    );
  };

  let displayedChart;
  switch (chartType) {
    case 'sales':
      displayedChart = (
        <SalesChart data={chartData} renderHeader={renderHeader} />
      );
      break;
    case 'revenue':
      displayedChart = (
        <RevenueChart data={chartData} renderHeader={renderHeader} />
      );
      break;
    case 'pnl':
      displayedChart = (
        <PnlChart data={chartData} renderHeader={renderHeader} />
      );
      break;
    case 'marketshare':
      displayedChart = (
        <MarketshareChart
          data={marketplaceChartData}
          renderHeader={renderHeader}
        />
      );
      break;
    default:
      displayedChart = (
        <RevenueChart data={chartData} renderHeader={renderHeader} />
      );
  }

  return (
    <Stack className={styles.dataInsightsChartSection}>
      <Stack className={styles.dataInsightsChartContainer}>
        {displayedChart}
      </Stack>
      <InsightsSection />
    </Stack>
  );
};
